export type SentryEventLike = {
    exception?: {
        values?: Array<{
            value?: string;
            stacktrace?: {
                frames?: Array<{
                    filename?: string | null;
                    abs_path?: string | null;
                }>;
            };
        }>;
    };
};

export const THIRD_PARTY_SCRIPT_PATTERNS = [
    /googletagmanager\.com/i,
    /\/gtm\.js/i,
];

export const FIRST_PARTY_SCRIPT_PATTERNS = [
    /\/resources\/js\//,
    /\/build\/assets\//,
    /\/@vite\//,
    /\/@fs\//,
];

function getFrameSource(frame: {
    filename?: string | null;
    abs_path?: string | null;
}): string {
    return `${frame.filename ?? ""} ${frame.abs_path ?? ""}`;
}

/**
 * Ignore errors that originate exclusively from third-party scripts such as GTM.
 * GTM custom tags often query DOM nodes that do not exist on every page, which
 * surfaces as noisy TypeErrors in Sentry without affecting our application code.
 */
export function isThirdPartyOnlyScriptError(event: SentryEventLike): boolean {
    const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
    if (frames.length === 0) {
        return false;
    }

    const hasThirdPartyFrame = frames.some((frame) =>
        THIRD_PARTY_SCRIPT_PATTERNS.some((pattern) =>
            pattern.test(getFrameSource(frame)),
        ),
    );
    if (!hasThirdPartyFrame) {
        return false;
    }

    const hasFirstPartyFrame = frames.some((frame) =>
        FIRST_PARTY_SCRIPT_PATTERNS.some((pattern) =>
            pattern.test(getFrameSource(frame)),
        ),
    );

    return !hasFirstPartyFrame;
}

export function beforeSendSentryEvent<T extends SentryEventLike>(
    event: T,
): T | null {
    return isThirdPartyOnlyScriptError(event) ? null : event;
}

export const sentryDenyUrls: Array<string | RegExp> = [
    /googletagmanager\.com/i,
    /gtm\.js/i,
];
